import { useState, useRef, useLayoutEffect } from 'react';

import { College } from '@backrs_ui/common-ui';

import { Box, Theme, SxProps } from '@mui/material';

import { HorizontalTabs } from './atoms/HorizontalTabs';
import { CollegeHeader } from './molecules/CollegeHeader';
import { MakeItPersonal } from './molecules/MakeItPersonal';
import { ReturnOnInvestment } from './molecules/ReturnOnInvestment';
import { TheCost } from './molecules/TheCost';
import { TheScoop } from './molecules/TheScoop';
import { WhoGetsIn } from './molecules/WhoGetsIn';

type CollegePageProps = Readonly<{
  college: College;
  handleBookmark: () => void;
  hasBookmarked: boolean;
}>;

export const CollegePage = ({ college, handleBookmark, hasBookmarked }: CollegePageProps) => {
  const [tabIndex, setTabIndex] = useState(0);
  const contentRef = useRef<HTMLDivElement | null>(null);

  const PAGE_SECTIONS = {
    THE_SCOOP: {
      id: 'the-scoop',
      title: 'The Scoop',
      component: <TheScoop college={college} />,
    },
    WHO_GETS_IN: {
      id: 'who-gets-in',
      title: 'Who Gets In',
      component: <WhoGetsIn college={college} />,
    },
    THE_COST: {
      id: 'the-cost',
      title: 'The Cost',
      component: <TheCost college={college} />,
    },
    ROI: {
      id: 'roi',
      title: 'ROI',
      component: <ReturnOnInvestment college={college} />,
    },
  } as const;

  const TAB_TITLES = Object.values(PAGE_SECTIONS).map((section) => section.title);
  const TAB_KEYS = Object.values(PAGE_SECTIONS).map((section) => section.id);

  useLayoutEffect(() => {
    const handleScroll = () => {
      if (!contentRef.current) return;

      const scrollPosition = contentRef.current.scrollTop + 150;

      // If we're near the top of the page, set tabIndex to -1
      if (contentRef.current.scrollTop < 100) {
        setTabIndex(-1);

        return;
      }

      const newIndex = TAB_KEYS.findIndex((sectionId) => {
        const section = document.getElementById(sectionId);

        if (section && contentRef.current) {
          const sectionTop = section.offsetTop - contentRef.current.offsetTop;

          return scrollPosition >= sectionTop && scrollPosition < sectionTop + section.offsetHeight;
        }

        return false;
      });

      if (newIndex !== -1) {
        setTabIndex(newIndex);
      }
    };

    const container = contentRef.current;

    if (container) {
      container.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
  }, [TAB_KEYS]);

  const handleTabChange = (newIndex: number) => {
    setTabIndex(newIndex);

    if (newIndex === -1) {
      // Scroll to the very top of the page on school name click
      contentRef.current?.scrollTo({
        top: 0,
        behavior: 'smooth',
      });

      return;
    }

    const element = document.getElementById(TAB_KEYS[newIndex]);

    if (element && contentRef.current) {
      const sectionTop = element.offsetTop - contentRef.current.offsetTop;

      contentRef.current.scrollTo({
        top: sectionTop - 48, // height of the tabs
        behavior: 'smooth',
      });
    }
  };

  return (
    <Box sx={containerSx}>
      <Box sx={mainContentSx} ref={contentRef}>
        <CollegeHeader
          college={college}
          handleBookmark={handleBookmark}
          hasBookmarked={hasBookmarked}
        />

        <HorizontalTabs
          tabs={TAB_TITLES}
          setValue={handleTabChange}
          value={tabIndex}
          schoolName={college.school_name}
        />

        <Box sx={contentContainerSx}>
          {Object.values(PAGE_SECTIONS).map((section) => (
            <Box key={section.id} id={section.id}>
              {section.component}
            </Box>
          ))}
        </Box>
      </Box>

      <MakeItPersonal collegeId={college.id} />
    </Box>
  );
};

const containerSx: SxProps<Theme> = {
  width: '100%',
  margin: '0 auto',
  display: {
    xs: 'block',
    md: 'flex',
  },
  flexDirection: {
    xs: 'column',
    md: 'row',
  },
  height: '100dvh',
};

const mainContentSx: SxProps<Theme> = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  maxWidth: { sm: '64vw', xl: '60vw' },
  '@media (min-width: 2000px)': {
    maxWidth: '56vw',
  },
  '@media (max-height: 768px)': {
    ml: '72px',
    maxWidth: '100%',
  },
  '@media (max-width: 414px)': {
    ml: '0px',
    maxWidth: '100%',
  },
  '@media (max-width: 900px)': {
    ml: '0px',
    maxWidth: '100%',
  },
  overflowY: 'auto',
};

const contentContainerSx: SxProps<Theme> = {
  maxWidth: '936px !important',
  alignSelf: 'flex-end',
  flex: 1,
  width: '100%',
};
